import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError, MONTHS } from "../adminApi";
import { Panel, buttonClass, inputClass } from "../components/Bits";

const emptyList = {
  name: "",
  description: "",
  segment: "",
  city: "",
  renewal_month: "",
  peo_status: "",
  min_score: "",
  min_participants: "",
  max_participants: "",
  fully_insured: false,
};

export default function AdminLists() {
  const [lists, setLists] = useState([]);
  const [form, setForm] = useState(emptyList);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminApi.get("/crm/lists").then(({ data }) => setLists(data.lists)).catch(() => {});

  useEffect(() => { load(); }, []);

  const createList = async (event) => {
    event.preventDefault();
    setBusy(true);
    const { name, description, ...rest } = form;
    const filters = {};
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== "" && value !== false) filters[key] = value;
    });
    try {
      await adminApi.post("/crm/lists", { name, description: description || null, filters });
      setForm(emptyList);
      toast.success("Target list saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openList = async (listId) => {
    try {
      const { data } = await adminApi.get(`/crm/lists/${listId}/accounts`, { params: { limit: 25 } });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeList = async (listId) => {
    if (confirmDelete !== listId) {
      setConfirmDelete(listId);
      return;
    }
    try {
      await adminApi.delete(`/crm/lists/${listId}`);
      setPreview(null);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (key) => (event) =>
    setForm((prev) => ({
      ...prev,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  return (
    <div className="space-y-6" data-testid="admin-lists-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Target lists</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Saved ABM segments
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/50">
          A list is a saved filter, not a frozen copy — accounts move in and out as filings refresh and
          stages change, so a sequence built on a list always targets the current reality.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="New list" testid="panel-new-list">
          <form onSubmit={createList} className="space-y-3" data-testid="list-form">
            <input required value={form.name} onChange={set("name")} placeholder="List name"
                   className={inputClass} data-testid="list-name-input" />
            <input value={form.description} onChange={set("description")} placeholder="What is this list for?"
                   className={inputClass} data-testid="list-description-input" />
            <select value={form.segment} onChange={set("segment")} className={inputClass}
                    data-testid="list-segment-select">
              <option value="">Any segment</option>
              <option value="PEO candidate">PEO candidate</option>
              <option value="Mid-market group">Mid-market group</option>
              <option value="Large group">Large group</option>
            </select>
            <input value={form.city} onChange={set("city")} placeholder="City (exact)"
                   className={inputClass} data-testid="list-city-input" />
            <select value={form.renewal_month} onChange={set("renewal_month")} className={inputClass}
                    data-testid="list-renewal-select">
              <option value="">Any renewal month</option>
              {MONTHS.map((month, index) => (
                <option key={month} value={index + 1}>Renews {month}</option>
              ))}
            </select>
            <select value={form.peo_status} onChange={set("peo_status")} className={inputClass}
                    data-testid="list-peo-select">
              <option value="">Any PEO status</option>
              <option value="on_peo">On a PEO (confirmed or likely)</option>
              <option value="confirmed">Confirmed PEO</option>
              <option value="likely">Likely on a PEO</option>
              <option value="unknown">No PEO signal</option>
            </select>
            <input type="number" min="0" max="100" value={form.min_score} onChange={set("min_score")}
                   placeholder="Min fit score" className={inputClass} data-testid="list-minscore-input" />
            <div className="flex gap-2">
              <input type="number" min="0" value={form.min_participants} onChange={set("min_participants")}
                     placeholder="Min lives" className={inputClass} data-testid="list-minlives-input" />
              <input type="number" min="0" value={form.max_participants} onChange={set("max_participants")}
                     placeholder="Max lives" className={inputClass} data-testid="list-maxlives-input" />
            </div>
            <label className="flex items-center gap-2 text-xs text-cream/60">
              <input type="checkbox" checked={form.fully_insured} onChange={set("fully_insured")}
                     data-testid="list-fullyinsured-checkbox" className="accent-[#D4AF37]" />
              Fully insured only
            </label>
            <button type="submit" disabled={busy} className={buttonClass} data-testid="list-submit">
              Save list
            </button>
          </form>
        </Panel>

        <Panel title="Lists" testid="panel-lists">
          <ul className="divide-y divide-cream/10">
            {lists.map((list) => (
              <li key={list.id} className="flex items-start justify-between gap-3 py-3"
                  data-testid={`list-row-${list.id}`}>
                <div className="min-w-0">
                  <button type="button" onClick={() => openList(list.id)}
                          data-testid={`list-open-${list.id}`}
                          className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                    {list.name}
                  </button>
                  <p className="mt-0.5 text-xs text-cream/40">{list.description || "No description"}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-mono text-xs text-cream/70">{list.count?.toLocaleString() ?? 0}</span>
                  <button type="button" onClick={() => removeList(list.id)}
                          data-testid={`list-delete-${list.id}`}
                          className={`text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                            confirmDelete === list.id ? "text-terra" : "text-cream/30 hover:text-terra"
                          }`}>
                    {confirmDelete === list.id ? "Tap to confirm" : "Delete"}
                  </button>
                </div>
              </li>
            ))}
            {lists.length === 0 ? (
              <li className="py-3 text-sm text-cream/40" data-testid="lists-empty">
                No lists yet. Save your first segment on the left.
              </li>
            ) : null}
          </ul>
        </Panel>

        <Panel title={preview ? `Preview · ${preview.list.name}` : "Preview"} testid="panel-list-preview">
          {!preview ? (
            <p className="text-sm text-cream/40">Open a list to preview its accounts.</p>
          ) : (
            <>
              <p className="mb-4 font-mono text-xs text-cream/45" data-testid="preview-total">
                {preview.total.toLocaleString()} accounts
              </p>
              <ul className="divide-y divide-cream/10">
                {preview.accounts.slice(0, 15).map((account) => (
                  <li key={account.id} className="py-2.5">
                    <Link to={`/admin/accounts/${account.id}`}
                          className="block truncate text-sm text-cream/85 transition-colors duration-200 hover:text-gold">
                      {account.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-cream/40">
                      {account.city} · {account.participants} lives · fit {account.score}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
